import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import {
  So5FixtureEvent,
  So5State,
} from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { SorareUser } from '@sorare/core/src/components/user/SorareUser';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { cart, glossary, transferMarket } from '@sorare/core/src/lib/glossary';
import {
  ClickAddToCart_Origin,
  ClickInstantBuy_Flow,
} from '@sorare/core/src/protos/events/so5/shared/events';

import { TokenDetailsRow } from 'components/ItemPreview/ui';
import { AddToCart } from 'components/cart/AddToCart';

import type { Props as PrimaryBuyBuyFieldProps } from '../PrimaryBuyBuyField';
import { PrimaryBuyBuyField } from '../PrimaryBuyBuyField';
import { PrimaryOfferPrice } from '../PrimaryBuyBuyField/Price';
import type { PrimaryBuyDetails_anyCard } from './__generated__/index.graphql';

export type Props = {
  card: PrimaryBuyDetails_anyCard;
  hideSorareUser?: boolean;
  isDesktopLayout: boolean;
  primaryBuyConfirmationOptions?: PrimaryBuyBuyFieldProps['primaryBuyConfirmationOptions'];
};

export const PrimaryBuyDetails = ({
  card,
  hideSorareUser,
  isDesktopLayout,
  primaryBuyConfirmationOptions,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  if (!card.livePrimaryOffer?.price) return null;

  return (
    <>
      <PrimaryOfferPrice primaryOffer={card.livePrimaryOffer} />
      {!hideSorareUser && (
        <LabelS color="var(--c-nd-600)">
          <FormattedMessage {...transferMarket.instantBuy} />
        </LabelS>
      )}
      {!!isDesktopLayout && !hideSorareUser && (
        <TokenDetailsRow>
          <SorareUser />
        </TokenDetailsRow>
      )}
      <Vertical>
        <PrimaryBuyBuyField
          size="small"
          color="quaternary"
          card={card}
          primaryBuyConfirmationOptions={
            primaryBuyConfirmationOptions || {
              actions: {
                primary: {
                  label: <FormattedMessage {...glossary.gotIt} />,
                },
                secondary: {
                  to: generateSportContextPath(
                    ANY_SPORT_PLAY_EVENTTYPE_FIXTURE,
                    {
                      sport: card.sport,
                      params: {
                        eventType: So5FixtureEvent.CLASSIC.toLowerCase(),
                        fixture: So5State.UPCOMING.toLowerCase(),
                      },
                    }
                  ),
                  label: <FormattedMessage {...glossary.playInPro} />,
                },
              },
            }
          }
          fullWidth={isDesktopLayout}
          origin={ClickInstantBuy_Flow.MARKETPLACE}
        />
        <AddToCart
          anyCard={card}
          size="small"
          color="tertiary"
          origin={ClickAddToCart_Origin.MARKETPLACE}
          fullWidth
        >
          <FormattedMessage {...cart.addToCart} />
        </AddToCart>
      </Vertical>
    </>
  );
};

PrimaryBuyDetails.fragments = {
  anyCard: gql`
    fragment PrimaryBuyDetails_anyCard on AnyCardInterface {
      slug
      sport
      livePrimaryOffer {
        id
        endDate
      }
      ...PrimaryBuyBuyField_anyCard
      ...AddToCart_anyCard
    }
    ${PrimaryBuyBuyField.fragments.anyCard}
    ${AddToCart.fragments.anyCard}
  ` as TypedDocumentNode<PrimaryBuyDetails_anyCard>,
};
